import React, {useState, useEffect} from 'react';
import {useDispatch} from 'react-redux';
import {useHistory, useLocation} from 'react-router-dom';
import {Buffer} from 'buffer';

import Env from '../../../util/env';
import {currentResource, labels} from '../../../util/utils';
import {serviceURL, userTheme, maxLengthInputs} from '../../../util/uiconstants';

import {DATE_FORMAT, DISPLAY_LANGUAGE, FIRST_NAME, LAST_NAME, NUMBER_FORMAT, THEME, TIME_ZONE} from './utils/constants';
import getDisplayLanguageOptions from './utils/getDisplayLanguageOptions';
import getDateFormatOptions from './utils/getDateFormatOptions';
import getNumberFormatOptions from './utils/getNumberFormatOptions';
import getTimeZoneOptions from './utils/getTimeZoneOptions';

import selectTimeZones from '../../../reducers/userpreferences/selectors/selectTimeZones';
import selectPaansValidity from '../../../reducers/Paans/selector/selectPaansValidity';
import selectUserPreferencesForForm from '../../../reducers/userpreferences/selectors/selectUserPreferencesForForm';
import {
	getTimezones,
	setUserPreferences,
	getUserPreferences,
	createUserPreferences
} from '../../../actions/UserPreferences/userpreferencesaction';
import {checkUserAcceptedPaansPolicy} from '../../../actions/Paans/paansaction';

// TODO: when the issue in CCS is fixed get it from CCS
// import {useForm} from '@ey/canvascoreservices';
import useForm from '../../CoreWrappers/Form/useForm';
import OutputText from '@ey/canvascoreservices/OutputText';

import TemplateFullPage from '../../Templates/TemplateFullPage';
import ContentSwitcher from '../../CoreWrappers/ContentSwitcher';
import Select from '../../CoreWrappers/Select';
import Button from '../../CoreWrappers/Button';
import Input from '../../CoreWrappers/Input';
import StyledUserSettings from './StyledUserSettings';
import LoadingWrapper from '../../CoreWrappers/LoadingWrapper';

const UserSettings = () => {
	const [isLoading, setIsLoading] = useState(true);

	const {isPaansDataLoaded, isPaansValid, paansRedirectUrl} = selectPaansValidity();
	const {isTimeZonesLoaded, timezones} = selectTimeZones();
	const {
		isNewUser,
		isPreferencesLoaded,
		userId,
		firstName,
		lastName,
		languageISOCode,
		dateFormat,
		numberFormat,
		timeZoneISOCode,
		theme
	} = selectUserPreferencesForForm();

	const history = useHistory();
	const location = useLocation();
	const dispatch = useDispatch();

	const showForm = isPreferencesLoaded && isTimeZonesLoaded;

	const displayLanguageOptions = getDisplayLanguageOptions(Env.isDevEnviorment());
	const dateFormatOptions = getDateFormatOptions();
	const numberFormatOptions = getNumberFormatOptions();
	const timeZoneOptions = getTimeZoneOptions(timezones);

	const handleGetPaansData = (dispatchCallback = null) => {
		const url = Env.getBaseURL(serviceURL) + 'Paans';
		dispatch(checkUserAcceptedPaansPolicy(url)).then(() => {
			if (dispatchCallback) dispatchCallback();
		});
	};

	const handleGetTimezomes = () => {
		const url = Env.getBaseURL(serviceURL) + 'timezones';
		dispatch(getTimezones(url));
	};

	const handleGetUserPreferences = (dispatchCallback) => {
		const url = Env.getBaseURL(serviceURL) + 'Users/Me';
		dispatch(getUserPreferences(url)).then(() => {
			if (isPaansValid) dispatchCallback();
			else handleGetPaansData(dispatchCallback);
		});
	};

	const handleSetUserPreferences = (payload) => {
		// send user id encoded in url because application gateway is not accepting some users
		const buff = new Buffer(userId);
		const base64UserId = buff.toString('base64');
		const url = Env.getBaseURL(serviceURL) + 'Users' + '/' + base64UserId;
		setIsLoading(true);
		dispatch(setUserPreferences(url, payload))
			.then(() => {
				handleGetUserPreferences(() => {
					location.state?.fromNavBar? history.goBack():history.push('/');
				});	
			})
			.catch(() => setIsLoading(false));
	};

	const handleCreateUserPreferences = (payload) => {
		const url = Env.getBaseURL(serviceURL) + 'Users';
		setIsLoading(true);
		dispatch(createUserPreferences(url, {body: payload}))
			.then(() => {
				handleGetUserPreferences(() => {
					// Navigate so page refreshes in case theres no data in the root page
					const currentUrl = window.location.href;
					const navigateTo = currentUrl.slice(0, currentUrl.lastIndexOf('/'));
					window.location.href = navigateTo;
				});
			})
			.catch(() => setIsLoading(false));
	};

	const handleRedirectToPaans = () => {
		window.location.href = paansRedirectUrl;
	};

	useEffect(() => {
		if (!isTimeZonesLoaded) handleGetTimezomes();
		else setIsLoading(false);
	}, [isTimeZonesLoaded]);

	useEffect(() => {
		if (isPaansDataLoaded && !isPaansValid && paansRedirectUrl !== null) handleRedirectToPaans();
	}, [isPaansDataLoaded, isPaansValid, paansRedirectUrl]);

	const initialValues = {
		[FIRST_NAME]: isNewUser ? '' : firstName,
		[LAST_NAME]: isNewUser ? '' : lastName,
		[DISPLAY_LANGUAGE]: isNewUser ? null : displayLanguageOptions?.find(({value}) => value === languageISOCode)?.id,
		[DATE_FORMAT]: isNewUser ? null : dateFormatOptions?.find(({label}) => label === dateFormat)?.id,
		[NUMBER_FORMAT]: isNewUser ? null : numberFormatOptions?.find(({label}) => label === numberFormat)?.id,
		[TIME_ZONE]: isNewUser ? null : timeZoneISOCode ?? null,
		[THEME]: isNewUser ? 'light' : theme
	};

	const schema = {
		[FIRST_NAME]: {required: true, maxLength: maxLengthInputs[FIRST_NAME]},
		[LAST_NAME]: {required: true, maxLength: maxLengthInputs[LAST_NAME]},
		[DISPLAY_LANGUAGE]: {required: true},
		[DATE_FORMAT]: {required: true},
		[NUMBER_FORMAT]: {required: true},
		[TIME_ZONE]: {required: true},
		[THEME]: {required: true}
	};

	const onSubmit = (values, callback) => {
		const findValue = (VALUEID) => ({id}) => id === values[VALUEID];

		const payload = {
			firstName: values[FIRST_NAME].trim(),
			lastName: values[LAST_NAME].trim(),
			languageISOCode: displayLanguageOptions?.find(findValue(DISPLAY_LANGUAGE))?.value,
			numberFormat: numberFormatOptions?.find(findValue(NUMBER_FORMAT))?.label,
			dateFormat: dateFormatOptions?.find(findValue(DATE_FORMAT))?.label,
			timeZoneISOCode: values[TIME_ZONE],
			theme: values[THEME]
		};

		if (isNewUser) handleCreateUserPreferences(payload);
		else handleSetUserPreferences(payload);
	};

	const {values, errors, handleChange, handleSubmit, handleReset, isFormValid} = useForm({
		initialValues,
		schema,
		onSubmit,
		labels: currentResource.formLabels
	});

	const renderHeader = (label, className = null) => {
		const props = {};
		if (className) props.className = className;
		return (
			<OutputText h2 {...props}>
				{label}
			</OutputText>
		);
	};

	const renderTextInput = (id, className = null) => {
		const props = {
			id,
			value: values[id],
			onChange: (e) => handleChange(id, e.target.value),
			label: labels[id],
			maxLength: maxLengthInputs[id]
		};

		if (className) props.className = className;

		if (errors[id]) props.errorMessage = errors[id]?.message;

		return <Input {...props} />;
	};

	const renderSelect = (id, options, className = null, filter = false) => {
		const props = {
			id,
			value: values[id],
			onChange: (newValue) => handleChange(id, newValue),
			label: labels[id],
			options,
			filter
		};

		if (className) props.wrapperClass = className;

		if (errors[id]) props.errorMessage = errors[id]?.message;

		return <Select {...props} />;
	};

	const renderThemeSelector = (className = null) => {
		const id = THEME;

		const options = [
			{id: userTheme.light, label: labels.light},
			{id: userTheme.dark, label: labels.dark}
		];

		const props = {
			id,
			options,
			value: options.find((item) => item.id === values[id]),
			onChange: (newValue) => handleChange(id, newValue.id)
		};

		if (className) props.className = className;

		return <ContentSwitcher {...props} />;
	};

	const renderSave = () => {
		const props = {
			label: labels.save,
			disabled: isLoading,
			onClick: handleSubmit,
			size: 'medium'
		};

		return <Button {...props} />;
	};

	const renderCancel = () => {
		const props = {
			label: labels.cancelLabel,
			variant: 'ghost',
			disabled: isLoading,
			onClick: () => history.goBack(),
			size: 'medium'
		};

		return <Button {...props} />;
	};

	return (
		<TemplateFullPage includeScroll={false}>
			{showForm && (
				<StyledUserSettings className="user-settings">
					<div className="customScrollbar">
						<LoadingWrapper className="grid" isLoading={isLoading}>
							{renderHeader(labels.usersettings, 'userSettings')}
							{renderTextInput(FIRST_NAME, 'firstName')}
							{renderTextInput(LAST_NAME, 'lastName')}
							{renderSelect(DISPLAY_LANGUAGE, displayLanguageOptions, 'displayLanguage')}
							{renderSelect(DATE_FORMAT, dateFormatOptions, 'dateFormat')}
							{renderSelect(NUMBER_FORMAT, numberFormatOptions, 'numberFormat')}
							{renderSelect(TIME_ZONE, timeZoneOptions, 'timeZone', true)}
							<div className="theme">
								{renderHeader(labels.theme)}
								<OutputText light>{labels.chooseDisplayTheme}</OutputText>
							</div>
							{renderThemeSelector('themeSelector')}
						</LoadingWrapper>
					</div>
					<footer>
						{!isNewUser && renderCancel()}
						{renderSave()}
					</footer>
				</StyledUserSettings>
			)}
		</TemplateFullPage>
	);
};

export default UserSettings;
